// Claim form validation.
//
// THE ROUND 14 GOTCHA APPLIES HERE. Zod skips an object-level `.superRefine`
// when the object parse aborts — any required field still blank — so a
// cross-field rule written inside the schema silently does not fire on a
// partly-filled form. This project has now hit that three times (student≠parent
// email, mailing address, phone format), and each fix was the same: move the
// real rule into a STANDALONE function called explicitly at every gate, and
// leave the schema holding only a lenient shape check.
//
// So: `claimShapeSchema` checks presence and shape. `claimIssues()` owns every
// rule that depends on more than one field. Both run at both gates —
// client submit and the identical server gate — via `validateClaim()`.

import { z } from "zod";
import {
  isStrictUsPhone,
  PHONE_STRICT_MESSAGE,
} from "../../../shared/src/validation/index.js";
import { ENTITY_TYPES, entityTypeHasNumber, isEntityType } from "./entity-types.js";

export interface ClaimFormInput {
  entityType: string;
  entityName: string;
  entityNumber: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  paymentMethod: string;
  squareOrderNumber: string;
  paymentNote: string;
  notes: string;
  /** One of FAMILY_CONTACT_CHOICES' codes. Required. */
  familyContact: string;
  /** Required when familyContact is "other". */
  familyContactOther: string;
}

export interface ClaimIssue {
  /** Field key, matching ClaimFormInput — drives the form's error map. */
  path: keyof ClaimFormInput;
  message: string;
}

/**
 * Whether the sponsor has a personal connection to the student's family.
 *
 * Directors use this to see whether sponsored students are getting local
 * community engagement or purely financial backing, and to target outreach.
 *
 * STORED AS A CODE, never the sentence. This wording is still settling, and
 * if the display sentence were what SharePoint held then every rewording
 * would silently split historical data into two categories that mean the
 * same thing. The code survives rewording; the label is presentation.
 */
export const FAMILY_CONTACT_CHOICES = [
  { code: "yes", label: "Yes — we’ve already made personal contact with the family" },
  { code: "not-yet", label: "Not yet, but we’d like to — may need help getting in touch" },
  { code: "none", label: "No — this is financial support only, no personal contact planned" },
  { code: "other", label: "Other" },
] as const;

export type FamilyContactCode = (typeof FAMILY_CONTACT_CHOICES)[number]["code"];

/** Short label for the admin card and the CSV export. */
export function familyContactLabel(code: string): string {
  return FAMILY_CONTACT_CHOICES.find((c) => c.code === code)?.label ?? code;
}

export const PAYMENT_METHODS = ["Square", "Check", "Other"] as const;

export const emptyClaimForm = (): ClaimFormInput => ({
  entityType: "",
  entityName: "",
  entityNumber: "",
  contactName: "",
  contactEmail: "",
  contactPhone: "",
  paymentMethod: "",
  squareOrderNumber: "",
  paymentNote: "",
  notes: "",
  familyContact: "",
  familyContactOther: "",
});

/**
 * LENIENT shape only. Deliberately does NOT encode any rule that depends on
 * another field — those live in claimIssues() for the reason at the top.
 */
export const claimShapeSchema = z.object({
  entityType: z.string().trim().min(1, "Choose the kind of organization."),
  entityName: z.string().trim().max(200),
  entityNumber: z.string().trim().max(40),
  contactName: z.string().trim().min(1, "Enter your name."),
  contactEmail: z
    .string()
    .trim()
    .min(1, "Enter your email address.")
    .regex(/^[^@\s]+@[^@\s]+\.[^@\s]+$/, "That doesn't look like an email address."),
  contactPhone: z.string().trim().min(1, "Enter a phone number."),
  paymentMethod: z.string().trim().min(1, "Choose how you're paying."),
  squareOrderNumber: z.string().trim().max(100),
  paymentNote: z.string().trim().max(2000),
  notes: z.string().trim().max(2000),
});

const trim = (v: unknown): string => String(v ?? "").trim();

/**
 * Every rule that depends on more than one field. Runs standalone at every
 * gate, on whatever the form holds right now — including a half-filled form,
 * which is exactly the case a schema-level superRefine would miss.
 */
/**
 * `squareOffered` says whether THIS claim's program takes card payment.
 *
 * Defaults to true so every existing caller and test is unchanged. Passed as
 * false for a program that is checks-and-Other-only, which makes the rule a
 * SERVER rule rather than a UI one: hiding the radio is not enforcement, and
 * a crafted request could otherwise submit a payment method that program has
 * no way to complete.
 */
export function claimIssues(
  input: ClaimFormInput,
  opts: { squareOffered?: boolean } = {}
): ClaimIssue[] {
  const squareOffered = opts.squareOffered !== false;
  const issues: ClaimIssue[] = [];
  const entityType = trim(input.entityType);
  const entityName = trim(input.entityName);
  const entityNumber = trim(input.entityNumber);
  const method = trim(input.paymentMethod);
  const order = trim(input.squareOrderNumber);
  const note = trim(input.paymentNote);
  const phone = trim(input.contactPhone);
  const family = trim(input.familyContact);
  const familyOther = trim(input.familyContactOther);

  // ---- organization -------------------------------------------------------
  if (entityType && !isEntityType(entityType)) {
    issues.push({ path: "entityType", message: "Choose one of the listed options." });
  } else if (entityType) {
    if (entityTypeHasNumber(entityType)) {
      // Post, Auxiliary Unit, SAL Squadron and ALR Chapter all carry a number,
      // and it is the number — not the name — that identifies the local
      // organization. The name stays optional for these.
      if (!entityNumber) {
        issues.push({
          path: "entityNumber",
          message: `Enter the ${entityType} number.`,
        });
      } else if (!/\d/.test(entityNumber)) {
        issues.push({ path: "entityNumber", message: "The number should contain digits." });
      }
    } else {
      // "Other" — a person or a business, which may have no number at all.
      // The free-text description IS the identity, so it is required here.
      if (!entityName) {
        issues.push({
          path: "entityName",
          message: "Describe who is sponsoring (a person's name, or a business).",
        });
      }
      if (entityNumber) {
        issues.push({
          path: "entityNumber",
          message: "Leave the number blank for this option, or choose a different type.",
        });
      }
    }
  }

  // ---- payment ------------------------------------------------------------
  if (method && !PAYMENT_METHODS.includes(method as (typeof PAYMENT_METHODS)[number])) {
    issues.push({ path: "paymentMethod", message: "Choose one of the listed options." });
  } else if (method === "Square" && !squareOffered) {
    issues.push({
      path: "paymentMethod",
      message:
        "Card payment is not available for this program. Choose Check or Other.",
    });
  } else if (method === "Square") {
    if (!order) {
      issues.push({
        path: "squareOrderNumber",
        message: "Enter the order number from your Square receipt.",
      });
    }
  } else if (method) {
    if (order) {
      issues.push({
        path: "squareOrderNumber",
        message: "An order number only applies to a Square payment.",
      });
    }
    // A note is required for "Other" and optional for "Check".
    //
    // DELIBERATE NARROWING of this plan's §2.5, which said a note was required
    // for both. "Check mailed to HQ" is self-describing, so demanding a note
    // adds friction to the commonest non-Square path for no information gain.
    // "Other" genuinely needs one — without it the payment method is unknown.
    if (method === "Other" && !note) {
      issues.push({ path: "paymentNote", message: "Say how you're paying." });
    }
  }

  // ---- contact ------------------------------------------------------------
  //
  // Strict US format, reusing the shared validator. Unlike an applicant, a
  // sponsoring entity is a US Legion organization, so the addressType-aware
  // branch of phoneIssues() does not apply — this is always the US rule.
  if (phone && !isStrictUsPhone(phone)) {
    issues.push({ path: "contactPhone", message: PHONE_STRICT_MESSAGE });
  }

  // ---- connection to the student -----------------------------------------
  //
  // Lives HERE, not in a Zod superRefine, for the reason recorded in the
  // build plan §2.5: Zod skips an object-level refine when the object parse
  // aborts, so a cross-field rule silently does not fire on a partly-filled
  // form. That has bitten this project four times (Round 14 email, Round 22
  // address, Round 24 phone, Round 26 Square).
  if (!family) {
    issues.push({
      path: "familyContact",
      message: "Choose one — this tells the director whether the student has local support.",
    });
  } else if (!FAMILY_CONTACT_CHOICES.some((c) => c.code === family)) {
    issues.push({ path: "familyContact", message: "Choose one of the listed options." });
  } else if (family === "other" && !familyOther) {
    issues.push({
      path: "familyContactOther",
      message: "Tell us briefly what your connection is.",
    });
  }
  if (family !== "other" && familyOther) {
    issues.push({
      path: "familyContactOther",
      message: "This only applies when you choose Other.",
    });
  }
  return issues;
}

export interface ClaimValidation {
  ok: boolean;
  /** field -> first message, ready to render beneath each input. */
  errors: Partial<Record<keyof ClaimFormInput, string>>;
}

/**
 * The one gate. Runs the lenient schema AND the standalone cross-field rules,
 * so neither can be forgotten at a call site. Used identically by the client
 * before submit and by the server on receipt.
 */
export function validateClaim(
  input: ClaimFormInput,
  opts: { squareOffered?: boolean } = {}
): ClaimValidation {
  const errors: Partial<Record<keyof ClaimFormInput, string>> = {};

  const parsed = claimShapeSchema.safeParse(input);
  if (!parsed.success) {
    for (const issue of parsed.error.issues) {
      const key = issue.path[0] as keyof ClaimFormInput | undefined;
      if (key && !errors[key]) errors[key] = issue.message;
    }
  }

  // Always runs, even when the schema above aborted — that is the entire point.
  for (const issue of claimIssues(input, opts)) {
    if (!errors[issue.path]) errors[issue.path] = issue.message;
  }

  return { ok: Object.keys(errors).length === 0, errors };
}

export { ENTITY_TYPES };
